/**
 * Bridges the existing SVG preview pages (CertificateDetails,
 * EditIssuerTemplate, CertificateIssue) to HTML templates. SVG keeps using
 * GenerateCertificate exactly as before; HTML is rendered in a sandboxed
 * iframe with the same placeholder substitution the backend performs.
 */
import React from "react";

import { createRoot } from "react-dom/client";

import HtmlCertificateFrame from "./HtmlCertificateFrame";
import { dateTimeRegex } from "../../../configs/App.Regex";
import { isHtmlTemplate } from "../../../configs/templateTypes";
import GenerateCertificate from "../../../service/GenerateCertificate";
import { DEFAULT_PAGE_SIZE, readHtmlPageSize } from "../model/pageSize";
import { renderHtml } from "../serializer/renderHtml";

const roots = new WeakMap();

const HTML_DOCUMENT_START =
  /^\uFEFF?\s*(?:<!--[\s\S]*?-->\s*)*<(?:!doctype\s+html|html[\s>])/i;

export const isHtmlTemplateSource = (text, templateType) =>
  isHtmlTemplate(templateType) || HTML_DOCUMENT_START.test(String(text || ""));

const pageSizeOf = (html) => readHtmlPageSize(html) || DEFAULT_PAGE_SIZE;

/** Form values may hold upload arrays ([{ url }]); templates need plain strings. */
function flattenValues(fields = {}) {
  return Object.fromEntries(
    Object.entries(fields || {}).map(([key, value]) => {
      if (Array.isArray(value)) return [key, value[0]?.url || value[0] || ""];
      if (value && typeof value === "object") return [key, value.url || ""];
      return [key, value ?? ""];
    }),
  );
}

const formatIsoDate = (value) => {
  const [year, month, day] = value.slice(0, 10).split("-");
  return `${day}/${month}/${year}`;
};

export function previewValues(fields = {}) {
  const values = flattenValues(fields);
  const first = String(values.recipientFirstName ?? "").trim();
  const last = String(values.recipientLastName ?? "").trim();
  if (first || last) values.recipientName = `${first} ${last}`.trim();
  Object.entries(values).forEach(([key, value]) => {
    if (typeof value === "string" && dateTimeRegex.test(value)) {
      values[key] = formatIsoDate(value);
    }
  });
  return values;
}

function mountHtml(container, html) {
  let root = roots.get(container);
  if (!root) {
    // eslint-disable-next-line no-param-reassign
    container.innerHTML = "";
    root = createRoot(container);
    roots.set(container, root);
  }
  const { width, height } = pageSizeOf(html);
  root.render(
    <HtmlCertificateFrame
      html={html}
      width={width}
      height={height}
      fitParentHeight
    />,
  );
}

/** First paint of a fetched template (placeholders visible). */
export function injectTemplatePreview(container, text, templateType) {
  if (!container) return;
  if (isHtmlTemplateSource(text, templateType))
    mountHtml(container, renderHtml(text, {}, { missing: "token" }));
  // eslint-disable-next-line no-param-reassign
  else container.innerHTML = text;
}

/** Re-render with the latest form values. */
export function updateTemplatePreview(container, text, fields, templateType) {
  if (!container || !text) return;
  if (isHtmlTemplateSource(text, templateType)) {
    mountHtml(
      container,
      renderHtml(text, previewValues(fields), { missing: "token" }),
    );
    return;
  }
  // eslint-disable-next-line no-param-reassign
  container.innerHTML = GenerateCertificate(text, fields);
}
